import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/EduShell";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AiControls } from "@/components/AiControls";
import { EduPeopleManager } from "@/components/EduPeopleManager";
import { MODE_LABEL, normalisePolicy, type AiPolicy, type AiPolicyMode } from "@/lib/ai-policy";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/education-admin")({
  component: EducationAdmin,
  head: () => ({
    meta: [
      { title: "Education controls - Inquira" },
      { name: "description", content: "Platform controls for the Inquira classroom features, including rollout switches and teacher roles." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Inquira education controls" },
      { property: "og:description", content: "Rollout switches for classroom features." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const FLAG_LABELS: Record<string, string> = {
  teacher_portal_enabled: "Teacher portal",
  school_portal_enabled: "School portal",
  classroom_ai_tools_enabled: "AI teacher tools",
};

interface Flag { key: string; enabled: boolean; description: string | null }

function EducationAdmin() {
  const { user, isPaid, signOut } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [email, setEmail] = useState("");
  const [counts, setCounts] = useState<{ classes: number; teachers: number; students: number } | null>(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [allowedModes, setAllowedModes] = useState<AiPolicyMode[]>(["full", "guided", "hints_only", "study", "disabled"]);
  const [ceiling, setCeiling] = useState(50);
  const [defaultPolicy, setDefaultPolicy] = useState<AiPolicy | null>(null);

  const load = useCallback(async () => {
    if (!user) { setIsAdmin(false); return; }
    const { data: admin } = await supabase.rpc("has_role", { _user_id: user.id, _role: "super_admin" });
    setIsAdmin(admin === true);
    if (admin !== true) return;
    const [{ data: f }, { count: classes }, { data: roles }] = await Promise.all([
      supabase.from("feature_flags").select("key, enabled, description").order("key"),
      supabase.from("classes").select("id", { count: "exact", head: true }),
      supabase.from("education_roles").select("role"),
    ]);
    setFlags((f as Flag[]) ?? []);
    const { data: settings } = await supabase
      .from("ai_platform_settings")
      .select("teacher_controlled_ai_enabled, allowed_modes, default_policy, max_interaction_ceiling")
      .maybeSingle();
    if (settings) {
      const row = settings as {
        teacher_controlled_ai_enabled: boolean;
        allowed_modes: string[] | null;
        default_policy: unknown;
        max_interaction_ceiling: number | null;
      };
      setAiEnabled(row.teacher_controlled_ai_enabled);
      setAllowedModes((row.allowed_modes ?? []) as AiPolicyMode[]);
      setCeiling(row.max_interaction_ceiling ?? 50);
      setDefaultPolicy(normalisePolicy(row.default_policy));
    }
    const list = (roles as Array<{ role: string }>) ?? [];
    setCounts({
      classes: classes ?? 0,
      teachers: list.filter((r) => r.role === "teacher").length,
      students: list.filter((r) => r.role === "student").length,
    });
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  async function toggle(flag: Flag) {
    const { error } = await supabase.rpc("admin_set_feature_flag", { _key: flag.key, _enabled: !flag.enabled });
    if (error) { toast.error(error.message); return; }
    toast.success(`${FLAG_LABELS[flag.key] ?? flag.key} ${flag.enabled ? "turned off" : "turned on"}`);
    void load();
  }

  async function saveAiSettings() {
    const { error } = await supabase.rpc("admin_set_ai_platform_settings" as never, {
      _enabled: aiEnabled,
      _allowed_modes: allowedModes,
      _default_policy: defaultPolicy,
      _ceiling: ceiling,
    } as never);
    if (error) { toast.error(error.message); return; }
    toast.success(i18n.t("edu.education_admin.teacher_controlled_ai_settings_saved"));
    void load();
  }

  async function grantTeacher() {
    if (!email.trim()) return;
    const { error } = await supabase.rpc("admin_edu_grant_role" as never, {
      _user_id: email.trim(),
      _role: "teacher",
    } as never);
    if (error) { toast.error(error.message); return; }
    toast.success(i18n.t("edu.education_admin.teacher_access_granted"));
    setEmail("");
    void load();
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} isPaid={isPaid} onSignOut={signOut} />
      <main className="mx-auto w-full max-w-3xl px-4 py-8 pb-24">
        <h1 className="font-display text-2xl font-black tracking-tight text-ink"><T k="edu.education_admin.education_controls" /></h1>
        <p className="mt-1 text-sm text-ink-muted"><T k="edu.education_admin.rollout_switches_and_teacher_access_for_" /></p>

        {isAdmin === false && (
          <div className="mt-6">
            <EmptyState title={i18n.t("edu.education_admin.not_available")} body="This page is for platform administrators." />
            <Link to="/" className="mt-4 inline-block text-sm font-semibold text-neon-pink"><T k="edu.education_admin.back_home" /></Link>
          </div>
        )}

        {isAdmin && (
          <>
            {counts && (
              <div className="mt-6 grid grid-cols-3 gap-3">
                {[["Classes", counts.classes], ["Teachers", counts.teachers], ["Students", counts.students]].map(([label, value]) => (
                  <div key={String(label)} className="rounded-2xl border border-rule bg-card p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</p>
                    <p className="mt-1 font-display text-2xl font-black text-ink">{value}</p>
                  </div>
                ))}
              </div>
            )}

            <h2 className="mt-8 font-display text-lg font-bold text-ink"><T k="edu.education_admin.rollout_switches" /></h2>
            <ul className="mt-3 space-y-2">
              {flags.map((f) => (
                <li key={f.key} className="flex items-center justify-between rounded-2xl border border-rule bg-card p-4">
                  <div>
                    <p className="font-semibold text-ink">{FLAG_LABELS[f.key] ?? f.key}</p>
                    {f.description && <p className="text-xs text-ink-muted">{f.description}</p>}
                  </div>
                  <Button variant={f.enabled ? "default" : "outline"} onClick={() => toggle(f)}>
                    {f.enabled ? "On" : "Off"}
                  </Button>
                </li>
              ))}
            </ul>

            <h2 className="mt-8 font-display text-lg font-bold text-ink"><T k="edu.education_admin.teacher_controlled_ai" /></h2>
            <div className="mt-3 space-y-4 rounded-2xl border border-rule bg-card p-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-ink">
                <input type="checkbox" checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} />
                Let teachers control AI per classroom and assignment
              </label>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted"><T k="edu.education_admin.modes_teachers_may_choose" /></p>
                <div className="mt-2 flex flex-wrap gap-3">
                  {(Object.keys(MODE_LABEL) as AiPolicyMode[]).map((m) => (
                    <label key={m} className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={allowedModes.includes(m)}
                        onChange={(e) =>
                          setAllowedModes(
                            e.target.checked ? [...allowedModes, m] : allowedModes.filter((x) => x !== m),
                          )
                        }
                      />
                      {MODE_LABEL[m]}
                    </label>
                  ))}
                </div>
              </div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Maximum AI interactions a teacher can allow per assignment
                <input
                  type="number"
                  min={1}
                  className="mt-1 w-32 rounded-xl border border-rule bg-background px-3 py-2 text-sm font-normal normal-case text-ink"
                  value={ceiling}
                  onChange={(e) => setCeiling(Number(e.target.value))}
                />
              </label>
              {defaultPolicy && (
                <AiControls
                  value={defaultPolicy}
                  onChange={setDefaultPolicy}
                  title={i18n.t("edu.education_admin.platform_default_ai_policy")}
                  showTemplates={false}
                />
              )}
              <Button onClick={saveAiSettings}><T k="edu.education_admin.save_ai_settings" /></Button>
            </div>

            <EduPeopleManager />

            <h2 className="mt-8 font-display text-lg font-bold text-ink"><T k="edu.education_admin.grant_teacher_access" /></h2>
            <p className="mt-1 text-xs text-ink-muted"><T k="edu.education_admin.paste_the_account_id_from_the_users_list" /></p>
            <div className="mt-3 flex flex-wrap gap-2">
              <input
                className="min-w-[220px] flex-1 rounded-xl border border-rule bg-card px-3 py-2 text-sm text-ink"
                placeholder={i18n.t("edu.education_admin.teacher_account_id")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button onClick={grantTeacher}><T k="edu.education_admin.grant" /></Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
